import { getCurrentInstance, onBeforeUnmount } from "vue";

export const DEFAULT_TERMINAL_STATUSES = ["S", "E", "C"];

/**
 * Error con el que `poll()` rechaza cuando la corrida sigue en curso tras
 * `maxAttempts` consultas: el caller decide qué decirle al usuario
 * (`lastStatus` es la última respuesta leída).
 */
export class ImportRunPollTimeout extends Error {
    constructor(lastStatus) {
        super("La importación sigue procesándose.");
        this.name = "ImportRunPollTimeout";
        this.lastStatus = lastStatus;
    }
}

/**
 * Sondeo del estado de una corrida de import en RQ (patrón P/S/E del mayor,
 * ventas/costos y las planillas mensuales). `poll(fetchStatus)` consulta
 * `fetchStatus()` (una función que resuelve el objeto de estado, con al menos
 * `status`) cada `intervalMs` hasta que `status` sea terminal, y resuelve con
 * ese objeto; rechaza con `ImportRunPollTimeout` al agotar `maxAttempts` o con
 * el error de red si una consulta falla. `onTick(status)` corre en cada
 * lectura (p. ej. para pintar `progress`). Un solo sondeo activo a la vez:
 * llamar `poll` de nuevo cancela el anterior (que queda sin resolver). Se
 * detiene solo al desmontar el componente que lo creó en `setup()`.
 *
 * @param {Object} [options]
 * @param {number} [options.intervalMs=3000]
 * @param {number} [options.maxAttempts=100]
 * @param {string[]} [options.terminalStatuses=["S","E","C"]]
 */
export function useImportRunPolling(options = {}) {
    const {
        intervalMs = 3000,
        maxAttempts = 100,
        terminalStatuses = DEFAULT_TERMINAL_STATUSES,
    } = options;
    let timer = null;
    let generation = 0;

    function stop() {
        generation += 1;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    }

    function poll(fetchStatus, { onTick } = {}) {
        stop();
        const mine = generation;
        let attempts = 0;
        return new Promise((resolve, reject) => {
            const tick = () => {
                timer = null;
                Promise.resolve()
                    .then(fetchStatus)
                    .then((data) => {
                        if (mine !== generation) return; // cancelado por stop()/poll()
                        if (onTick) onTick(data);
                        if (terminalStatuses.includes(data && data.status)) {
                            resolve(data);
                            return;
                        }
                        attempts += 1;
                        if (attempts >= maxAttempts) {
                            reject(new ImportRunPollTimeout(data));
                            return;
                        }
                        timer = setTimeout(tick, intervalMs);
                    })
                    .catch((error) => {
                        if (mine !== generation) return;
                        reject(error);
                    });
            };
            tick();
        });
    }

    // Fuera de un setup() (p. ej. un util suelto en tests) no hay instancia:
    // el caller es responsable de llamar stop().
    if (getCurrentInstance()) {
        onBeforeUnmount(stop);
    }

    return { poll, stop };
}
